export const clubPortalQueryKeys = {
	clubPortal: {
		accounts: (clubId: string) => ["club-portal", "clubs", clubId, "accounts"] as const,
		announcements: (clubId: string) => ["club-portal", "clubs", clubId, "announcements"] as const,
		assessment: (clubId: string, userId: string) =>
			["club-portal", "clubs", clubId, "assessments", userId] as const,
		assessments: (clubId: string) => ["club-portal", "clubs", clubId, "assessments"] as const,
		attendance: (clubId: string, from: string, to: string) =>
			["club-portal", "clubs", clubId, "attendance", from, to] as const,
		auditLogs: (clubId: string, params?: Record<string, unknown>) =>
			["club-portal", "clubs", clubId, "audit-logs", params ?? {}] as const,
		availabilityForUser: (clubId: string, userId: string) =>
			["club-portal", "clubs", clubId, "availability", "user", userId] as const,
		// Member availability keys sit deliberately outside the events prefix:
		// an event mutation must not invalidate a member's own pattern.
		availabilityHeatmap: (clubId: string, params?: Record<string, unknown>) =>
			["club-portal", "clubs", clubId, "availability", params ?? {}] as const,
		availabilityMine: (clubId: string) =>
			["club-portal", "clubs", clubId, "availability", "me"] as const,
		bankAccounts: (clubId: string) => ["club-portal", "clubs", clubId, "banks"] as const,
		bankTransactions: (clubId: string, bankId: string) =>
			["club-portal", "clubs", clubId, "banks", bankId, "transactions"] as const,
		bookings: (clubId: string) => ["club-portal", "clubs", clubId, "bookings"] as const,
		cashBook: (clubId: string, from: string, to: string) =>
			["club-portal", "clubs", clubId, "reports", "kassenbuch", from, to] as const,
		chatInvites: (clubId: string) => ["club-portal", "clubs", clubId, "chat-invites"] as const,
		chatMessages: (clubId: string, roomId: string) =>
			["club-portal", "clubs", clubId, "chat-rooms", roomId, "messages"] as const,
		chatRoomInvites: (clubId: string, roomId: string) =>
			["club-portal", "clubs", clubId, "chat-rooms", roomId, "invites"] as const,
		chatRoomMembers: (clubId: string, roomId: string) =>
			["club-portal", "clubs", clubId, "chat-rooms", roomId, "members"] as const,
		chatRooms: (clubId: string) => ["club-portal", "clubs", clubId, "chat-rooms"] as const,
		clubCourts: (clubId: string, locationId: string) =>
			["club-portal", "clubs", clubId, "locations", locationId, "courts"] as const,
		clubLocations: (clubId: string) => ["club-portal", "clubs", clubId, "locations"] as const,
		clubSquads: (clubId: string) => ["club-portal", "clubs", clubId, "squads"] as const,
		clubSquadMembers: (clubId: string, squadId: string) =>
			["club-portal", "clubs", clubId, "squads", squadId, "members"] as const,
		clubSquadSessions: (clubId: string, squadId: string) =>
			["club-portal", "clubs", clubId, "squads", squadId, "sessions"] as const,
		courtBooking: (clubId: string) => ["club-portal", "clubs", clubId, "venue"] as const,
		creditNotes: (clubId: string, invoiceId: string) =>
			["club-portal", "clubs", clubId, "invoices", invoiceId, "creditNotes"] as const,
		customFields: (clubId: string) => ["club-portal", "clubs", clubId, "custom-fields"] as const,
		detail: (id: string) => ["club-portal", "clubs", id] as const,
		disputes: (clubId: string) => ["club-portal", "clubs", clubId, "disputes"] as const,
		documents: (clubId: string, folderId?: string) =>
			["club-portal", "clubs", clubId, "documents", folderId ?? "root"] as const,
		euerReport: (clubId: string, year?: number) =>
			["club-portal", "clubs", clubId, "reports", "euer", year] as const,
		eventDetail: (clubId: string, eventId: string) =>
			["club-portal", "clubs", clubId, "events", eventId] as const,
		// Nested under eventDetail, so refreshing one event refreshes its guest list.
		eventInvitees: (clubId: string, eventId: string) =>
			["club-portal", "clubs", clubId, "events", eventId, "invitees"] as const,
		events: (clubId: string, eventType?: string) =>
			["club-portal", "clubs", clubId, "events", eventType ?? "all"] as const,
		// Under the events prefix on purpose, so every existing event mutation
		// already refreshes the calendar without naming a new key.
		eventsRange: (clubId: string, from: Date, to: Date, eventType?: string) =>
			[
				"club-portal",
				"clubs",
				clubId,
				"events",
				"range",
				from.toISOString(),
				to.toISOString(),
				eventType ?? "all",
			] as const,
		expenses: (clubId: string) => ["club-portal", "clubs", clubId, "expenses"] as const,
		fiscalYears: (clubId: string) => ["club-portal", "clubs", clubId, "years"] as const,
		folders: (clubId: string, parentId?: string) =>
			["club-portal", "clubs", clubId, "folders", parentId ?? "root"] as const,
		invoiceDetail: (clubId: string, invoiceId: string) =>
			["club-portal", "clubs", clubId, "invoices", invoiceId] as const,
		invoices: (clubId: string, status?: string) =>
			["club-portal", "clubs", clubId, "invoices", status ?? "all"] as const,
		memberAudit: (clubId: string, memberId: string, params?: { limit?: number; offset?: number }) =>
			["club-portal", "clubs", clubId, "members", memberId, "audit", params ?? {}] as const,
		memberCustomValues: (clubId: string, userId: string) =>
			["club-portal", "clubs", clubId, "members", userId, "custom-values"] as const,
		memberNotes: (clubId: string, memberId: string) =>
			["club-portal", "clubs", clubId, "members", memberId, "notes"] as const,
		memberSubscriptions: (clubId: string, memberId: string) =>
			["club-portal", "clubs", clubId, "members", memberId, "subscriptions"] as const,
		members: (clubId: string) => ["club-portal", "clubs", clubId, "members"] as const,
		membershipFees: (clubId: string) => ["club-portal", "clubs", clubId, "fees"] as const,
		membershipTypes: (clubId: string) =>
			["club-portal", "clubs", clubId, "membership-types"] as const,
		newsletterDetail: (clubId: string, nlId: string) =>
			["club-portal", "clubs", clubId, "newsletters", nlId] as const,
		newsletters: (clubId: string) => ["club-portal", "clubs", clubId, "newsletters"] as const,
		paymentDashboard: (clubId: string) =>
			["club-portal", "clubs", clubId, "payments", "dashboard"] as const,
		payments: (clubId: string) => ["club-portal", "clubs", clubId, "payments"] as const,
		pollDetail: (clubId: string, pollId: string) =>
			["club-portal", "clubs", clubId, "polls", pollId] as const,
		polls: (clubId: string) => ["club-portal", "clubs", clubId, "polls"] as const,
		reflections: (clubId: string, eventId: string) =>
			["club-portal", "clubs", clubId, "events", eventId, "reflections"] as const,
		reflectionsSummary: (clubId: string, eventId: string) =>
			["club-portal", "clubs", clubId, "events", eventId, "reflections", "summary"] as const,
		reminderConfig: (clubId: string) => ["club-portal", "clubs", clubId, "reminders"] as const,
		skillLevels: (clubId: string) => ["club-portal", "clubs", clubId, "skill-levels"] as const,
		sphereSummary: (clubId: string, year?: number) =>
			["club-portal", "clubs", clubId, "reports", "spheres", year] as const,
		squadRatings: (clubId: string) =>
			["club-portal", "clubs", clubId, "squads", "ratings"] as const,
		stripeAccount: (clubId: string) => ["club-portal", "clubs", clubId, "stripe"] as const,
		subscriptions: (clubId: string) => ["club-portal", "clubs", clubId, "subscriptions"] as const,
		tasks: (clubId: string, status?: string) =>
			["club-portal", "clubs", clubId, "tasks", status ?? "all"] as const,
		trainingAvailability: (clubId: string, seasonId: string) =>
			["club-portal", "clubs", clubId, "training", "seasons", seasonId, "availability"] as const,
		trainingExclusionDates: (clubId: string, seasonId: string) =>
			["club-portal", "clubs", clubId, "training", "seasons", seasonId, "dates"] as const,
		trainingGroup: (clubId: string, groupId: string) =>
			["club-portal", "clubs", clubId, "training", "groups", groupId] as const,
		trainingGroupMembers: (clubId: string, groupId: string) =>
			["club-portal", "clubs", clubId, "training", "groups", groupId, "members"] as const,
		trainingGroupSuggestions: (clubId: string, groupId: string, query?: string) =>
			[
				"club-portal",
				"clubs",
				clubId,
				"training",
				"groups",
				groupId,
				"suggestions",
				query ?? "",
			] as const,
		trainingGroups: (clubId: string, seasonId?: string) =>
			["club-portal", "clubs", clubId, "training", "groups", seasonId ?? "all"] as const,
		trainingMyAvailability: (clubId: string, seasonId: string) =>
			[
				"club-portal",
				"clubs",
				clubId,
				"training",
				"seasons",
				seasonId,
				"availability",
				"me",
			] as const,
		trainingSeason: (clubId: string, seasonId: string) =>
			["club-portal", "clubs", clubId, "training", "seasons", seasonId] as const,
		trainingSeasons: (clubId: string, status?: string) =>
			["club-portal", "clubs", clubId, "training", "seasons", status ?? "all"] as const,
		trainingSessions: (clubId: string, seasonId: string) =>
			["club-portal", "clubs", clubId, "training", "seasons", seasonId, "sessions"] as const,
		trainingSlots: (clubId: string, groupId: string) =>
			["club-portal", "clubs", clubId, "training", "groups", groupId, "slots"] as const,
	},
} as const;
